/**
 * Reusable Date Range Filter Dropdown Component
 * Used for filtering date ranges.
 * Position via useFilterPopupPortal (parent) — do not self-position.
 */
import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { FILTER_POPUP_CLASS } from '../../../hooks/useFilterPopupPortal';

export const DateRangeFilterDropdown = ({ isOpen, onApply, onClose, currentRange }) => {
  const [startDate, setStartDate] = useState(currentRange?.start || '');
  const [endDate, setEndDate] = useState(currentRange?.end || '');

  useEffect(() => {
    if (isOpen) {
      setStartDate(currentRange?.start || '');
      setEndDate(currentRange?.end || '');
    }
  }, [isOpen, currentRange]);

  // UX: no auto-close on outside tap / scroll; close via X / Clear / Apply.

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ start: startDate, end: endDate });
    onClose();
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    onApply({ start: null, end: null });
    onClose();
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatForDateInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const handleDateChange = (value, setDate) => {
    if (!value) {
      setDate('');
      return;
    }
    setDate(value);
  };

  return (
    <div className={`${FILTER_POPUP_CLASS} z-50 bg-white border rounded-md shadow-lg w-full sm:w-80 max-w-xs`}>
      <div className="flex flex-col">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-white px-3 py-2">
          <div className="text-sm font-medium text-gray-700">Filter by Date Range</div>
          <button
            type="button"
            aria-label="Close filter"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500/30"
          >
            <FiX className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <div className="p-4 pt-3">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Start Date</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={startDate ? formatDateForDisplay(startDate) : ''}
                  placeholder="MM-DD-YYYY"
                  readOnly
                  className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500 cursor-pointer"
                />
                <input
                  type="date"
                  value={formatForDateInput(startDate)}
                  onChange={(e) => handleDateChange(e.target.value, setStartDate)}
                  className="absolute right-0 top-0 h-full px-2 py-1 opacity-0 cursor-pointer"
                  style={{ width: '2.5rem' }}
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                  aria-label="Calendar"
                  type="button"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">End Date</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={endDate ? formatDateForDisplay(endDate) : ''}
                  placeholder="MM-DD-YYYY"
                  readOnly
                  className="w-full px-2 py-1 text-sm font-normal border rounded focus:outline-none focus:border-red-500 cursor-pointer"
                />
                <input
                  type="date"
                  value={formatForDateInput(endDate)}
                  onChange={(e) => handleDateChange(e.target.value, setEndDate)}
                  className="absolute right-0 top-0 h-full px-2 py-1 opacity-0 cursor-pointer"
                  style={{ width: '2.5rem' }}
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                  aria-label="Calendar"
                  type="button"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
